import { KohaBiblioDto } from '../../types/api.types';
import { KohaItemDto } from '../../types/api.types';
import { BiblioDetailModel, HoldingItem } from '../../types/types';
import { cleanIsbd } from '../../shared/pipes/clean-isbd';
import { API_ENDPOINTS } from '../../core/constants/api-endpoints';

declare const require: any;

/**
 * Mapper: transforms raw Koha DTOs into the UI BiblioDetailModel.
 * Pure static class — no dependencies, easy to unit-test.
 */
export class BiblioDetailMapper {

  static toModel(dto: KohaBiblioDto, items: KohaItemDto[], marcJson?: any): BiblioDetailModel {
    return {
      id: dto.biblio_id,
      title: cleanIsbd(dto.title),
      subtitle: dto.subtitle ? cleanIsbd(dto.subtitle) : null,
      author: dto.author ? cleanIsbd(dto.author) : null,
      abstract: dto.abstract ?? null,
      notes: dto.notes ?? null,
      isbn: dto.biblioitem?.isbn ?? null,
      issn: dto.biblioitem?.issn ?? null,
      itemType: dto.biblioitem?.itemtype ?? 'UNKNOWN',
      itemTypeLabel: dto._strings?.['biblioitem.itemtype']?.str ?? dto.item_type ?? null,
      language: dto.biblioitem?.language ?? null,
      publisher: dto.biblioitem?.publisher ?? dto.publisher ?? null,
      publicationPlace: dto.biblioitem?.place ?? null,
      publicationYear: dto.biblioitem?.publication_year ?? null,
      pages: dto.biblioitem?.pages ?? dto.pages ?? null,
      callNumber: dto.biblioitem?.callnumber ?? null,
      coverImageUrl: BiblioDetailMapper.buildCoverImageUrl(dto),
      hijriDate: BiblioDetailMapper.formatHijriDate(dto.copyright_date),
      serial: dto.serial ?? false,
      holdings: items.map(BiblioDetailMapper.toHoldingItem),
      marcFields: marcJson ? BiblioDetailMapper.parseMarc(marcJson) : undefined,
    };
  }

  static parseMarc(marcJson: any) {
    const fields = [];
    if (marcJson?.leader) {
      fields.push({
        tag: '000',
        value: marcJson.leader,
      });
    }

    if (Array.isArray(marcJson?.fields)) {
      marcJson.fields.forEach((fieldObj: any) => {
        const tag = Object.keys(fieldObj)[0];
        const content = fieldObj[tag];
        
        if (typeof content === 'string') {
          // Control fields 001-009
          fields.push({
            tag,
            value: content,
          });
        } else if (typeof content === 'object') {
          // Data fields 010-999
          const subfields: { code: string; value: string }[] = [];
          if (Array.isArray(content.subfields)) {
            content.subfields.forEach((subObj: any) => {
              const code = Object.keys(subObj)[0];
              subfields.push({ code, value: subObj[code] });
            });
          }
          fields.push({
            tag,
            ind1: content.ind1,
            ind2: content.ind2,
            subfields,
          });
        }
      });
    }

    return fields;
  }

  static toHoldingItem(item: KohaItemDto): HoldingItem {
    let status: HoldingItem['status'] = 'available';
    if (item.lost_status !== 0) {
      status = 'lost';
    } else if (item.not_for_loan_status !== 0 || item.effective_not_for_loan_status !== 0) {
      status = 'not-for-loan';
    } else if (item.checked_out_date) {
      status = 'checked-out';
    }

    return {
      itemId: item.item_id,
      barcode: item.external_id ?? (item as any).barcode ?? null,
      externalId: item.external_id ?? (item as any).barcode ?? null,
      copyNumber: item.copy_number ?? item.inventory_number ?? null,
      collectionCode: item.collection_code ?? null,
      itemTypeId: item.effective_item_type_id ?? item.item_type_id ?? '',
      acquisitionDate: item.acquisition_date ?? null,
      status,
      homeLibraryId: item.home_library_id,
      homeLibraryName: item.home_library?.name ?? null,
      holdingLibraryId: item.holding_library_id,
      holdingLibraryName: item.holding_library?.name ?? null,
      publicNotes: item.public_notes ?? null,
      callNumber: item.callnumber ?? null,
    };
  }

  private static buildCoverImageUrl(dto: KohaBiblioDto): string | null {
    if (dto.cover_image_ids?.length && dto.cover_image_ids[0] != null) {
      return API_ENDPOINTS.coverImage(dto.biblio_id, dto.cover_image_ids[0]);
    }
    return null;
  }

  private static formatHijriDate(gregorianDate: string | null | undefined): string | null {
    if (!gregorianDate) return null;
    try {
      const moment = require('moment-hijri');
      const m = moment(gregorianDate, 'YYYY-MM-DD');
      if (!m.isValid()) return null;
      return m.format('iD iMMMM iYYYYهـ');
    } catch {
      return gregorianDate;
    }
  }
}
